import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnChanges, OnDestroy, OnInit, PLATFORM_ID, Output, SimpleChanges, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { HttpClientModule } from '@angular/common/http';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ListWithTagComponent } from "../list-with-tag/list-with-tag.component";
import { FilterPopoverComponent } from '../filter-popover/filter-popover.component';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatPaginatorComponent } from '../mat-paginator/mat-paginator.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { BankMasterService } from '../../Services/Common/bank-master.service';
import { ShimmerLoaderComponent } from "../shimmer-loader/shimmer-loader.component";
import { AlertService } from '../../Services/notifications/alert.service';
import { environment } from '../../../environments/environment';

interface CellFragment {
  text: string;
  url?: string;
  isHtml?: boolean;
}

interface StructuredCellConfig {
  fragments: CellFragment[];
}

type TableCellValue = string | string[];
type TableUrlCell = string | string[] | StructuredCellConfig | CellFragment[];
type TableUrlRow = string | TableUrlCell[];

@Component({
  selector: 'app-table',
  imports: [MatTableModule, MatSortModule, MatPaginatorComponent, MatDialogModule, CommonModule, MatIconModule, ListWithTagComponent, MatRadioModule, FormsModule, MatFormFieldModule, HttpClientModule, FilterPopoverComponent, RouterModule, MatTooltipModule, ShimmerLoaderComponent],
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  private static instanceCount = 0;


  searchText = ''
  //is loading is completed or still pending
  @Input() load: boolean = false;
  //Heading H5
  @Input() tableInfo!: string;
  // this parameter will have table heading
  @Input() displayedColumns!: string[]
  // if want to pass table data (without URL)
  @Input() tableData!: TableCellValue[][];
  // this parameter will have URL's respected to table
  @Input() tableUrls: TableUrlRow[] = []
  //for which heading(column) we want specific row span/ paragraph with list
  @Input() differentForColomn!: string
  // If
  isParagraphWithList: boolean = false
  @Input() listTag!: string
  @Input() listItems!: any
  @Input() isRowWithSpans: boolean = false
  @Input() isColWithSpans: boolean = false
  @Input() hasLinks: boolean = false
  @Input() tableType!: string
  @Input() dataLoadError = false; // true when API failed to load data
  @Input() dataLoadComplete = false; // true once API call finishes so empty state can show
  @Input() responseData?: any;
  @Input() pageSize: number = 5;//for showing rows
  @Input() infoColumns?: string[];
  @Input() selectedValue?: any;
  @Input() noPagination?: any; //don't want pagination pass it as true.
  @Output() selectedValueChange: EventEmitter<any> = new EventEmitter<any>();
  private readonly portalBaseUrl = (environment.portalUrl || '').replace(/\/+$/, '');
  @Input() usePortalBaseForRelativeLinks = false;
  private skipNextClick = false;


  @Input() externalDataSource?: any[];
  dataSource!: any;
  rowSpans: any = []
  colSpans: Map<number, number[]> = new Map();
  isMultipleParagraphs: boolean = false
  isNestedRows: boolean = false
  colIndex: number = 0;
  showInput!: boolean
  teamMember: boolean = false
  /** New: internal columns used by template always */
  internalColumns: string[] = [];
  /** New: whether there are no headers */
  noHeaders = false;
  private filteredRows: any[] = [];

  private _ignoreNextToggle = false;
  allColumns: string[] = [];

  currentPage = 1;
  visiblePages: number[] = [];
  // pageSize = this.rowSizeOfPage;
  filteringData: any;
  // status filter options loaded from JSON
  statusOptions: string[] = [];
  // true when parent component passed `filterOptions` (even an empty array)
  parentFilterOptionsProvided = false;
  selectedStatuses: Set<string> = new Set();
  showFilterPopover = false;
  popoverPosition = { top: 0, left: 0 };
  isSmallScreen = false;
  private columnIndexCache = new Map<string, number>();
  private removeDocumentClickListener?: () => void;
  private readonly documentClickHandler = (event: MouseEvent) => this.handleDocumentClick(event);
  private pointerInside = false;
  private refreshInProgress = false;

  @Input() sortableFields: string[] = []
  @Input() rightAlignedColumns: string[] = []
  @Input() centerAlignedColumns: string[] = []
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('tableRoot') tableRoot?: ElementRef<HTMLElement>;
  @Input() showPagination: boolean = false;
  @Input() showFilter: boolean = true
  @Input() showActionIcons: boolean = false;
  // If provided, compute distinct options from this column name (or use as filter key)
  @Input() filterColumn?: string;
  // Optional explicit filter options passed from parent JSON/templates
  @Input() filterOptions?: string[];

  @Input() externalSearchText?: string;
  @Input() externalActiveFiltersCount: number = 0;

  @Output() filtersCleared: EventEmitter<void> = new EventEmitter<void>();
  @Output() selectionChange: EventEmitter<number> = new EventEmitter<number>();
  selectedRadioIndex: number | null = null;



  constructor(private cdr: ChangeDetectorRef, private dialog: MatDialog, private http: HttpClient, private alertService: AlertService, private router: Router, private hostRef: ElementRef<HTMLElement>, @Inject(DOCUMENT) private doc: Document, @Inject(PLATFORM_ID) private platformId: Object, private bankMasterService: BankMasterService) {
    TableComponent.instanceCount += 1;
  }

  /** Returns true when this table's root element is visible (not display:none and within viewport) */
  private isTableVisible(): boolean {
    try {
      const root = this.getTableRootElement();
      if (!root) return false;
      // check computed style
      const style = window.getComputedStyle(root);
      if (!style || style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
      // ensure element or one of its ancestors is not hidden via HTML details/accordion summary patterns
      let el: HTMLElement | null = root;
      while (el) {
        const s = window.getComputedStyle(el);
        if (!s || s.display === 'none' || s.visibility === 'hidden') return false;
        el = el.parentElement;
      }
      // basic bounding rect check: if entirely outside viewport, treat as not visible
      const rect = root.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return false;
      return true;
    } catch (err) {
      return true;
    }
  }

  private getTableRootElement(): HTMLElement | null {
    if (this.tableRoot?.nativeElement) {
      return this.tableRoot.nativeElement;
    }
    if ((this.hostRef as any)?.nativeElement) {
      return this.hostRef.nativeElement;
    }
    return null;
  }

  private hasOpenDialog(): boolean {
    try {
      return !!(this.dialog && Array.isArray((this.dialog as any).openDialogs) && (this.dialog as any).openDialogs.length > 0);
    } catch (err) {
      return false;
    }
  }

  private hasMultipleTableInstances(): boolean {
    return TableComponent.instanceCount > 1;
  }

  private hasEditAction(): boolean {
    if (this.tableType === 'radiotable' || this.tableType === 'teamMember' || this.showActionIcons) {
      return true;
    }
    const columns = this.collectColumns();
    return columns.has('edit') || columns.has('editIcon');
  }

  private hasDeleteAction(): boolean {
    if (this.tableType === 'radiotable' || this.tableType === 'teamMember' || this.showActionIcons || this.tableInfo === "Client's List") {
      return true;
    }
    const columns = this.collectColumns();
    return columns.has('delete') || columns.has('deleteIcon') || columns.has('deleteAction');
  }
  //shortcuts just enabled for radiotable type for now
  private areShortcutsEnabled(): boolean {
    return this.tableType === 'radiotable';
  }

  private collectColumns(): Set<string> {
    const set = new Set<string>();
    (Array.isArray(this.displayedColumns) ? this.displayedColumns : []).forEach(col => {
      if (col) set.add(col);
    });
    (Array.isArray(this.allColumns) ? this.allColumns : []).forEach(col => {
      if (col) set.add(col);
    });
    return set;
  }

  private refreshTableData(reason: 'shortcut' | 'manual' = 'manual'): void {
    if (this.refreshInProgress) {
      // console.log('[Table] refresh already in progress, ignoring additional request');
      return;
    }
    this.refreshInProgress = true;
    // console.log('[Table] refresh triggered', reason);
    const refreshPromise = this.createDataSource();
    Promise.resolve(refreshPromise).then(() => {
      try {
        this.filteringData = this.getFilteredData(this.dataSource);
      } catch (err) {
        console.warn('Failed to reapply filters after refresh', err);
      }
      this.cdr.detectChanges();
      if (reason === 'shortcut') {
        try {
          this.alertService?.show?.('Table refreshed', { type: 'info', duration: 1200 });
        } catch (e) {
          // ignore alert errors
        }
      }
    }).catch(err => {
      console.error('Error refreshing table data', err);
    }).finally(() => {
      this.refreshInProgress = false;
    });
  }

  onSelectRow(rowIndex: number) {
    this.selectedRadioIndex = rowIndex;
    try {
      this.selectionChange.emit(rowIndex);
    } catch (e) { }
  }

  /**
   * Keyboard shortcut handler: Alt+E should trigger edit for the selected row.
   * Only active when not focused on an input/textarea/contenteditable.
   */
  @HostListener('window:keydown', ['$event'])
  handleGlobalKeydown(event: KeyboardEvent) {
    try {
      // console.log('[Table] keydown received', { key: event.key, alt: event.altKey, ctrl: event.ctrlKey, meta: event.metaKey });
      //try { this.alertService && this.alertService.show && this.alertService.show && this.alertService.show(`Key: ${event.key} alt:${event.altKey} ctrl:${event.ctrlKey}` , { type: 'info', duration: 800 }); } catch(e) {}
      const target = event.target as HTMLElement | null;
      // Ignore if focusing an input, textarea, or contenteditable element
      if (target) {
        const tag = target.tagName?.toLowerCase();
        const isEditable = tag === 'input' || tag === 'textarea' || target.isContentEditable;
        if (isEditable) return;
      }

      if (!this.isActiveTableContext()) {
        return;
      }
      if (event.altKey && !event.ctrlKey && !event.metaKey && (event.key === 'r' || event.key === 'R')) {
        // console.log('[Table] Refresh shortcut detected');
        try { event.preventDefault(); } catch (e) { }

        try {
          if (!this.isTableVisible()) {
            // console.log('[Table] table not visible - ignoring refresh shortcut');
            return;
          }
        } catch (err) {
          // ignore
        }

        if (this.hasOpenDialog()) {
          // console.log('[Table] dialog open - skipping refresh shortcut');
          return;
        }

        this.refreshTableData('shortcut');
        return;
      }
      //remove if shortcuts should be enabled for all table types
      if (!this.areShortcutsEnabled()) {
        return;
      }

      // Only respond to Alt+E (case-insensitive)
      if (event.altKey && !event.ctrlKey && !event.metaKey && (event.key === 'e' || event.key === 'E')) {
        // console.log('[Table] Alt+E detected');
        // prevent the default browser behavior for Alt+E where applicable
        try { event.preventDefault(); } catch (e) { }

        if (!this.hasEditAction()) {
          // console.log('[Table] edit shortcut ignored because no edit action is available');
          return;
        }

        // Ignore when table is hidden
        try {
          if (!this.isTableVisible()) {
            // console.log('[Table] table not visible - ignoring edit shortcut');
            return;
          }
        } catch (err) {
          // ignore
        }

        // Avoid stacking dialogs
        if (this.hasOpenDialog()) {
          // console.log('[Table] dialog already open - skipping edit shortcut');
          return;
        }

        // If tableType is radiotable or teamMember, use selectedRadioIndex
        // For teamMember, prefer selectedRadioIndex but fall back to the first row if none selected
        if (this.tableType === 'teamMember') {
          // console.log('[Table] tableType is teamMember; selectedRadioIndex=', this.selectedRadioIndex);
          let idx = (typeof this.selectedRadioIndex === 'number' && this.selectedRadioIndex >= 0) ? this.selectedRadioIndex : 0;
          const row = this.dataSource && Array.isArray(this.dataSource) ? this.dataSource[idx] : null;
          if (row) {
            this.editRow(row, 'editTeamMember');
          }
        } else if (this.tableType === 'radiotable') {
          if (typeof this.selectedRadioIndex === 'number' && this.selectedRadioIndex >= 0) {
            const row = this.dataSource && Array.isArray(this.dataSource) ? this.dataSource[this.selectedRadioIndex] : null;
            if (row) {
              this.editRow(row, 'editBankAccount');
            }
          }
        } else {
          // Generic fallback: use selected index if present
          if (typeof this.selectedRadioIndex === 'number' && this.selectedRadioIndex >= 0) {
            const row = this.dataSource && Array.isArray(this.dataSource) ? this.dataSource[this.selectedRadioIndex] : null;
            if (row) {
              this.editRow(row, 'edit');
            }
          }
        }
      }


      // Prefer Ctrl+Alt+D for delete because Alt+D is commonly captured by browsers
      if ((event.altKey && event.ctrlKey && (event.key === 'd' || event.key === 'D')) ||
        (event.altKey && !event.ctrlKey && !event.metaKey && (event.key === 'd' || event.key === 'D'))) {
        // console.log('[Table] Delete shortcut detected', { key: event.key, alt: event.altKey, ctrl: event.ctrlKey });
        //  try { this.alertService && this.alertService.show && this.alertService.show('Delete shortcut detected', { type: 'info', duration: 800 }); } catch(e) {}
        try { event.preventDefault(); } catch (e) { }

        if (!this.hasDeleteAction()) {
          // console.log('[Table] delete shortcut ignored because no delete action is available');
          return;
        }

        // If this table is not currently visible (e.g., collapsed accordion panel), ignore shortcut
        try {
          if (!this.isTableVisible()) {
            // console.log('[Table] table not visible - ignoring delete shortcut');
            return;
          }
        } catch (err) {
          // ignore visibility check errors
        }

        // If a dialog is already open, don't open another one
        if (this.hasOpenDialog()) {
          // console.log('[Table] a dialog is already open - ignoring delete shortcut');
          return;
        }

        // Determine target row similar to edit handling
        let targetRow: any = null;
        if (this.tableType === 'teamMember') {
          const idx = (typeof this.selectedRadioIndex === 'number' && this.selectedRadioIndex >= 0) ? this.selectedRadioIndex : 0;
          targetRow = this.dataSource && Array.isArray(this.dataSource) ? this.dataSource[idx] : null;
        } else {
          if (typeof this.selectedRadioIndex === 'number' && this.selectedRadioIndex >= 0) {
            targetRow = this.dataSource && Array.isArray(this.dataSource) ? this.dataSource[this.selectedRadioIndex] : null;
          }
        }

        if (targetRow) {
          // eventType depends on tableType; choose a reasonable default
          const type = this.tableType === 'teamMember' ? 'deleteTeamMember' : (this.tableType === 'radiotable' ? 'deleteBankAccount' : 'delete');
          this.deleteRow(targetRow, type);
        }
      }
    } catch (err) {
      // ignore handler errors
    }
  }

  @HostListener('document:mousedown', ['$event'])
  handleDocumentMouseDown(event: MouseEvent) {
    this.updatePointerFocus(event);
  }

  @HostListener('document:touchstart', ['$event'])
  handleDocumentTouchStart(event: TouchEvent) {
    this.updatePointerFocus(event);
  }

  private updatePointerFocus(event: Event) {
    try {
      const root = this.getTableRootElement();
      if (!root) {
        this.pointerInside = false;
        return;
      }
      const target = event.target as Node | null;
      this.pointerInside = !!target && root.contains(target);
    } catch (err) {
      this.pointerInside = false;
    }
  }

  private isActiveTableContext(): boolean {
    if (!this.hasMultipleTableInstances()) {
      return true;
    }
    const root = this.getTableRootElement();
    if (!root) return false;
    const active = this.doc?.activeElement as HTMLElement | null;
    if (active && root.contains(active)) {
      return true;
    }
    return this.pointerInside;
  }

  async ngOnInit() {
    // console.log('Table Data', this.tableData)
    // console.log('Table Heading', this.displayedColumns)
    // console.log("URL", this.tableUrls);
    // console.log("sorting Feilds", this.sortableFields);
    this.prepareColumns();
    this.getTableType();
    await this.createDataSource()
    this.updateVisiblePages();
    // ensure columns are computed on init as well
    this.updateActionColumns();
    // if parent passed explicit filterOptions mark it to avoid overwrites
    if (Array.isArray(this.filterOptions)) {
      this.parentFilterOptionsProvided = true;
      this.statusOptions = [...this.filterOptions];
    }
    this.loadStatusOptions(true);
    this.isSmallScreen = window.innerWidth <= 768;

    try {
      if (this.router && this.router.url && this.router.url.toString().toLowerCase() === '/account/bank-details') {
        try { this.bankMasterService.load(); } catch (e) { /* ignore */ }
      }
    } catch (e) { /* ignore */ }
  }

  ngAfterViewInit(): void {
    this.setupDocumentClickListener();
    setTimeout(() => {
      try {
        if (this.sort && this.filteringData && 'sort' in this.filteringData) {
          (this.filteringData as MatTableDataSource<any>).sort = this.sort;
        } else if (this.sort && this.dataSource && 'sort' in (this.dataSource as any)) {
          (this.dataSource as any).sort = this.sort;
        }
      } catch (err) {
        console.warn('Unable to attach sort to table data source', err);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.removeDocumentClickListener) {
      this.removeDocumentClickListener();
      this.removeDocumentClickListener = undefined;
    }
    TableComponent.instanceCount = Math.max(0, TableComponent.instanceCount - 1);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.prepareColumns();
    this.getTableType();
    this.createDataSource();
    // recompute status options when inputs or data change - force reload
    if (Array.isArray(this.filterOptions)) {
      this.parentFilterOptionsProvided = true;
      this.statusOptions = [...this.filterOptions];
    }
    this.loadStatusOptions(true);
    // recompute action columns when inputs change
    this.updateActionColumns();
    // showFilter controls only whether the filter UI is shown.
    // Action icons/columns are controlled independently by `showActionIcons`.
    if (this.showActionIcons) {
      const extras = [] as string[];
      if (!this.displayedColumns.includes('editIcon')) extras.push('editIcon');
      if (!this.displayedColumns.includes('deleteIcon')) extras.push('deleteIcon');
      if (extras.length) {
        this.displayedColumns = [...this.displayedColumns, ...extras];
      }
    }

    const filtered = this.displayedColumns.filter(col => col !== 'id' && col !== 'Id' && !col.startsWith('_'));
    // If the parent explicitly requested action icon columns for other table variants
    if (this.showActionIcons) {
      const extras = [] as string[];
      if (!this.displayedColumns.includes('editIcon')) extras.push('editIcon');
      if (!this.displayedColumns.includes('deleteIcon')) extras.push('deleteIcon');
      if (extras.length) {
        this.displayedColumns = [...this.displayedColumns, ...extras];
      }
    }

    // Always include 'edit'/'delete' columns for radiotable and teamMember table types
    const baseCols = [...filtered];
    if (this.tableType === 'radiotable' || this.tableType === 'teamMember') {
      baseCols.push('edit', 'delete');
    }

    // if (this.tableType === 'radiotable') {
    //   if (!baseCols.includes('select')) baseCols.unshift('select');
    //   else {
    //     baseCols.splice(baseCols.indexOf('select'), 1);
    //     baseCols.unshift('select');
    //   }
    // }

    // Also ensure edit/delete is included when showActionIcons is true (legacy flag)
    if (this.showActionIcons) {
      baseCols.push('edit', 'delete');
    }

    this.allColumns = Array.from(new Set(baseCols));
    // console.log("All Columns", this.allColumns);
  }

  // Consolidated logic to ensure edit/delete columns exist when needed
  private updateActionColumns(): void {
    const filtered = (Array.isArray(this.displayedColumns) ? this.displayedColumns : []).filter(col => col !== 'id' && col !== 'Id' && !col.startsWith('_'));

    if (this.showActionIcons) {
      const extras = [] as string[];
      if (!this.displayedColumns.includes('editIcon')) extras.push('editIcon');
      if (!this.displayedColumns.includes('deleteIcon')) extras.push('deleteIcon');
      if (extras.length) {
        this.displayedColumns = [...this.displayedColumns, ...extras];
      }
    }

    const baseCols = [...filtered];
    if (this.tableType === 'radiotable' || this.tableType === 'teamMember') {
      baseCols.push('edit', 'delete');
    }
    if (this.showActionIcons) {
      baseCols.push('edit', 'delete');
    }

    this.allColumns = Array.from(new Set(baseCols));
  }

  private prepareColumns(): void {
    const hasExplicitHeadings = Array.isArray(this.displayedColumns) && this.displayedColumns.length > 0;
    this.noHeaders = !hasExplicitHeadings;

    if (hasExplicitHeadings) {
      this.internalColumns = this.displayedColumns.filter(col => col !== 'id' && col !== 'Id' && !col.startsWith('_'));
    } else {
      const maxLen = Array.isArray(this.tableData) && this.tableData.length
        ? Math.max(...this.tableData.map(row => Array.isArray(row) ? row.length : 0))
        : 0;
      this.internalColumns = Array.from({ length: maxLen }, (_, idx) => `Column ${idx + 1}`);
    }

    this.columnIndexCache.clear();
  }

  getOriginalIndex(heading: string): number {
    if (!heading) return -1;
    if (this.columnIndexCache.has(heading)) {
      return this.columnIndexCache.get(heading)!;
    }

    let index = -1;
    if (Array.isArray(this.displayedColumns) && this.displayedColumns.length) {
      index = this.displayedColumns.indexOf(heading);
      if (index === -1 && heading === 'select') {
        index = this.displayedColumns.indexOf('');
      }
    }

    if (index === -1) {
      index = this.internalColumns.indexOf(heading);
    }

    this.columnIndexCache.set(heading, index);
    return index;
  }

  getChipUrl(row: any, heading: string, chipIndex: number): string | null {
    if (!row || chipIndex == null || chipIndex < 0) return null;

    const actualRowIndex = typeof row['_originalIndex'] === 'number'
      ? row['_originalIndex']
      : Array.isArray(this.dataSource)
        ? this.dataSource.indexOf(row)
        : -1;
    if (actualRowIndex == null || actualRowIndex < 0) return null;

    const colIndex = this.getOriginalIndex(heading);
    if (colIndex < 0) return null;

    const urls = this.getCellUrls(actualRowIndex, colIndex);
    if (!Array.isArray(urls) || urls.length === 0) return null;

    if (chipIndex < urls.length) {
      return urls[chipIndex] || null;
    }

    return urls[0] || null;
  }

  // Clipboard / contextmenu / drop prevention handlers for search inputs
  private _clipboardBlockTimeout: any = null;

  preventPaste(evt: ClipboardEvent | any) {
    try { evt.preventDefault(); } catch (e) { /* ignore */ }
    this.showBlockedClipboardMessage();
  }

  preventCopy(evt: ClipboardEvent | any) {
    try { evt.preventDefault(); } catch (e) { /* ignore */ }
    this.showBlockedClipboardMessage();
  }

  preventCut(evt: ClipboardEvent | any) {
    try { evt.preventDefault(); } catch (e) { /* ignore */ }
    this.showBlockedClipboardMessage();
  }

  preventContext(evt: Event | any) {
    try { evt.preventDefault(); } catch (e) { /* ignore */ }
    this.showBlockedClipboardMessage();
  }

  preventDrop(evt: DragEvent | any) {
    try { evt.preventDefault(); } catch (e) { /* ignore */ }
    this.showBlockedClipboardMessage();
  }

  private showBlockedClipboardMessage() {
    if (this._clipboardBlockTimeout) return;
    this._clipboardBlockTimeout = setTimeout(() => { this._clipboardBlockTimeout = null; }, 1500);
    try {
      this.alertService.show("For security reasons this field does not allow copy or paste.", { type: 'info', duration: 3000 });
    } catch (e) {
      // ignore
    }
  }

  getTableType() {
    if (this.tableType === 'multiPara') {
      this.isMultipleParagraphs = true;
      this.tableData = this.convertTableData(this.tableData);
    } else if (this.tableType === 'nestedRows') {
      this.isNestedRows = true;
      this.tableData = this.convertNestedRowsData(this.tableData);
    } else if (this.tableType?.includes('rowWithSpan')) {
      this.isRowWithSpans = true;
      const parts = this.tableType.split('-');
      this.colIndex = parseInt(parts[1] || '0', 10);

      if (!this.noHeaders) {
        this.differentForColomn = this.displayedColumns[this.colIndex];
      } else {
        this.differentForColomn = this.internalColumns[this.colIndex];
      }
    } else if (this.tableType?.includes('colWithSpan')) {
      this.isColWithSpans = true;
    } else if (this.tableType?.includes('paraWithList')) {
      this.isParagraphWithList = true;
      const parts = this.tableType.split('-');
      this.colIndex = parseInt(parts[1] || '0', 10);
      if (!this.noHeaders) {
        this.differentForColomn = this.displayedColumns[this.colIndex];
      } else {
        this.differentForColomn = this.internalColumns[this.colIndex];
      }
    }
    else if (this.tableType === 'radiotable') {
      this.displayedColumns = this.displayedColumns.map(h => h === '' ? 'select' : h);
    }
    else if (this.tableType === 'teamMember') {
      this.teamMember = true;

      //this.displayedColumns = this.displayedColumns.map(h => h === '' ? 'select' : h);
    } else if (this.tableType === 'withoutHeading') {
      this.dataSource = this.tableData
    }

    if (this.tableType !== 'withoutHeading') {
      this.createDataSource();
    }
  }

  async createDataSource() {
    const cols = (Array.isArray(this.displayedColumns) && this.displayedColumns.length > 0)
      ? this.displayedColumns
      : this.internalColumns;

    if (Array.isArray(this.externalDataSource) && this.externalDataSource.length) {
      this.dataSource = this.externalDataSource.map((row: any, index: number) => ({ ...row, _originalIndex: index }));
    } else {
      this.dataSource = (Array.isArray(this.tableData) ? this.tableData : []).map((row, index) => {
        const obj: any = {};
        obj['_originalIndex'] = index;
        cols.forEach((col, idx) => {
          obj[col] = this.normalizeCellValue(row?.[idx]);
        });
        return obj;
      });
    }
    this.filteredRows = Array.isArray(this.dataSource) ? [...this.dataSource] : [];
    this.showPagination = !this.noPagination && (this.filteredRows.length > this.pageSize);
    this.currentPage = 1;
    this.goToPage(1);
    if (this.tableType === "sorting" && !this.noPagination) {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      let filteringData = this.dataSource.slice(startIndex, endIndex);
      this.filteringData = new MatTableDataSource(filteringData);
      this.filteringData.sort = this.sort;
    }

    if (this.isRowWithSpans) {
      this.rowSpans = await this.calculateRowSpan()
    }
    if (this.isColWithSpans) {
      this.colSpans = await this.calculateColSpan();
    }
    if (this.tableType === 'radiotable' && !this.noPagination && (this.selectedRadioIndex === null || this.selectedRadioIndex === undefined)) {
      if (Array.isArray(this.dataSource) && this.dataSource.length > 0) {
        this.selectedRadioIndex = 0;
        try { this.selectionChange.emit(0); } catch (e) { }
      }
    }

    // Ensure selectedRadioIndex stays within bounds after any data changes
    try {
      if (!Array.isArray(this.dataSource) || this.dataSource.length === 0) {
        this.selectedRadioIndex = null;
      } else if (typeof this.selectedRadioIndex !== 'number' || this.selectedRadioIndex < 0) {
        // If no valid selection, default to first row
        this.selectedRadioIndex = 0;
        try { this.selectionChange.emit(this.selectedRadioIndex); } catch (e) { }
      } else if (this.selectedRadioIndex >= this.dataSource.length) {
        // If selected index points past the end (e.g., last row was deleted), move to last row
        this.selectedRadioIndex = this.dataSource.length - 1;
        try { this.selectionChange.emit(this.selectedRadioIndex); } catch (e) { }
      }
    } catch (err) {
      // ignore any clamping errors
    }

    this.cdr.detectChanges();
  }

  loadStatusOptions(forceReload: boolean = false) {
    // debug: show current inputs when loading

    // If parent supplied filter options (even an empty array), prefer them
    // and skip the HTTP lookup — an explicit empty array means "no options".
    if (Array.isArray(this.filterOptions)) {
      this.statusOptions = [...this.filterOptions];
      this.cdr.detectChanges();
      return;
    }

    // Prefer explicit filter options provided via component input first
    if (!forceReload && this.statusOptions && this.statusOptions.length > 0) {
      this.cdr.detectChanges();
      return;
    }

    if (forceReload) {
      this.statusOptions = [];
    }

    this.cdr.detectChanges();
  }

  toggleStatusSelection(option: string) {
    if (this.selectedStatuses.has(option)) this.selectedStatuses.delete(option);
    else this.selectedStatuses.add(option);
  }
  applyStatusFilter() {
    this.showFilterPopover = false;
    this._ignoreNextToggle = true;
    if (this.selectedStatuses.size > 0) {
      this.filteringData = this.getFilteredData(this.dataSource);
      this.filteredRows = this.filteringData;
      this.currentPage = 1;
      this.showPagination = !this.noPagination && (this.filteredRows.length > this.pageSize);
      this.goToPage(1);
    } else {
      this.resetStatusFilter();
    }
  }
  resetStatusFilter() {
    this.selectedStatuses.clear();
    this._ignoreNextToggle = true;
    this.showFilterPopover = false;
    const filtered = this.getFilteredData(this.dataSource);
    this.filteredRows = filtered;
    this.currentPage = 1;
    this.showPagination = !this.noPagination && (this.filteredRows.length > this.pageSize);
    this.goToPage(1);
  }

  togglePopover(event: MouseEvent) {
    // event.stopPropagation();
    if (this._ignoreNextToggle) {
      this._ignoreNextToggle = false;
      try { event.stopPropagation(); } catch { }
      return;
    }
    const nextState = !this.showFilterPopover;
    this.showFilterPopover = nextState;
    if (nextState) {
      this.loadStatusOptions(true);
    }
    try {
      const target = (event.currentTarget || event.target) as HTMLElement;
      const filterLabel = (target.closest && (target.closest('.textHover') as HTMLElement)) || target;
      let iconEl: HTMLElement | null = null;
      if (filterLabel && filterLabel.querySelector) {
        iconEl = filterLabel.querySelector('mat-icon') as HTMLElement | null;
      }
      if (!iconEl) {
        if (target && target.tagName && target.tagName.toLowerCase() === 'mat-icon') {
          iconEl = target as HTMLElement;
        } else {
          iconEl = null;
        }
      }
      const rect = (iconEl && iconEl.getBoundingClientRect()) || (filterLabel && filterLabel.getBoundingClientRect());

      // find nearest scrollable wrapper; prefer radio-table-wrapper or table container
      const wrapper = (filterLabel && (filterLabel.closest && (filterLabel.closest('.radio-table-wrapper') as HTMLElement))) ||
        (filterLabel && (filterLabel.closest && (filterLabel.closest('.table-container') as HTMLElement))) ||
        (filterLabel && (filterLabel.closest && (filterLabel.closest('.mat-table') as HTMLElement))) || document.body;
      const wrapperRect = wrapper.getBoundingClientRect();

      // compute position relative to wrapper so popover scrolls with it
      const top = (rect ? (rect.bottom - wrapperRect.top + (wrapper.scrollTop || 0) + 6) : 8);
      const provisionalCenter = rect ? (rect.left - wrapperRect.left + rect.width / 2 + (wrapper.scrollLeft || 0)) : (wrapper.clientWidth / 2);

      this.popoverPosition = { top, left: provisionalCenter };
      this.cdr.detectChanges();

      // after popover renders, measure width and clamp within wrapper
      setTimeout(() => {
        try {
          const pop = (wrapper.querySelector && ((wrapper.querySelector('.filter-popover') as HTMLElement) || (wrapper.querySelector('.filter-popover-inner') as HTMLElement))) ||
            (document.querySelector('.filter-popover') as HTMLElement) || (document.querySelector('.filter-popover-inner') as HTMLElement) as HTMLElement;
          if (pop && wrapper) {
            const popW = pop.offsetWidth;
            let finalLeft = provisionalCenter - popW / 2;
            const maxLeft = Math.max(8, wrapper.clientWidth - popW - 8);
            if (finalLeft > maxLeft) finalLeft = maxLeft;
            if (finalLeft < 8) finalLeft = 8;
            this.popoverPosition.left = finalLeft;
            this.cdr.detectChanges();
          }
        } catch (e) {
          // ignore measurement errors
        }
      }, 0);
      // console.log('togglePopover', this.showFilterPopover, this.popoverPosition, target);
    } catch (err) {
      console.warn('Could not compute popover position', err);
    }
  }

  private setupDocumentClickListener(): void {
    if (this.removeDocumentClickListener || !isPlatformBrowser(this.platformId)) {
      return;
    }
    if (!this.doc) {
      return;
    }
    const handler = this.documentClickHandler;
    this.doc.addEventListener('click', handler, true);
    this.removeDocumentClickListener = () => {
      this.doc.removeEventListener('click', handler, true);
    };
  }

  private handleDocumentClick(event: MouseEvent): void {
    if (!this.showFilterPopover) {
      return;
    }
    const root = this.tableRoot?.nativeElement || this.hostRef?.nativeElement;
    if (!root) {
      return;
    }

    const targetNode = (event.target as Node) || null;
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];

    const triggerEl = root.querySelector('.filter-trigger');
    const popoverHost = root.querySelector('.filter-popover');
    const popoverInner = root.querySelector('.filter-popover-inner');

    const containsTarget = (el: Element | null) => {
      if (!el || !targetNode) {
        return false;
      }
      if (el.contains(targetNode)) {
        return true;
      }
      return Array.isArray(path) && path.includes(el);
    };

    if (containsTarget(triggerEl) || containsTarget(popoverHost) || containsTarget(popoverInner)) {
      return;
    }

    this.showFilterPopover = false;
    this.cdr.markForCheck();
  }

  @HostListener('window:resize')
  onResize() {
    const wasSmall = this.isSmallScreen;
    this.isSmallScreen = window.innerWidth <= 768;
    if (wasSmall !== this.isSmallScreen) {
      this.showFilterPopover = false;
      this.cdr.detectChanges();
    }
  }
  search() { }

  convertTableData(tableData: TableCellValue[][]): TableCellValue[][] {
    return tableData.map(row => {
      if (row.length > 1) {
        const [type, ...taxEntries] = row;
        const combinedTax = taxEntries.map(entry => this.normalizeCellValue(entry)).join('\n');
        return [this.normalizeCellValue(type), combinedTax];
      }
      return row;
    });
  }

  convertNestedRowsData(tableData: any[]): string[][] {
    const result: string[][] = [];

    tableData.forEach(item => {
      if (Array.isArray(item)) {
        // Regular row
        result.push(item);
      } else if (item.mainRow && item.subRows) {
        // Nested row structure
        result.push([item.mainRow, '']);
        item.subRows.forEach((subRow: string[], index: number) => {
          result.push(subRow);
        });
      }
    });

    return result;
  }

  isNestedSubRow(rowIndex: number): boolean {
    if (!this.isNestedRows) return false;

    // Check if this row is a sub-row (starts with (a), (b), (c), etc.)
    const rowData = this.dataSource[rowIndex];
    const firstColumn = rowData[this.internalColumns[0]];
    return firstColumn && firstColumn.match(/^\([a-z]\)/);
  }

  isLastNestedSubRow(rowIndex: number): boolean {
    if (!this.isNestedRows || !this.isNestedSubRow(rowIndex)) return false;

    // Check if the next row is not a nested sub-row
    const nextRowIndex = rowIndex + 1;
    if (nextRowIndex >= this.dataSource.length) return true;

    return !this.isNestedSubRow(nextRowIndex);
  }

  async calculateRowSpan() {
    const spans: any = [];
    let lastSimilar: any = null;
    let lastIndex = 0;
    let count = 0;

    this.dataSource.forEach((row: any, i: number) => {
      if (row[this.differentForColomn] === lastSimilar) {
        count += 1;
        spans[lastIndex] = count;
        spans[i] = 0;
      } else {
        lastSimilar = row[this.differentForColomn];
        lastIndex = i;
        count = 1;
        spans[i] = count;
      }

    });
    return spans;
  }

  editRow(data: any, page: string) {
    try {
      const idx = (typeof data === 'object' && data !== null && typeof data._originalIndex === 'number') ? data._originalIndex : this.dataSource.indexOf(data);
      if (typeof idx === 'number' && idx >= 0) {
        this.selectedRadioIndex = idx;
        try { this.selectionChange.emit(idx); } catch (e) { }
      }
    } catch (e) { }
    // console.log(data, "data:----")
    let dialogWidth = '90vw';
    import('../../Pages/common/dialog/dialog.component').then(m => {
      this.dialog.open(m.DialogComponent, {
        width: dialogWidth,
        height: 'auto',
        maxWidth: '95vw',
        panelClass: 'custom-dialog-container',
        autoFocus: false,
        data: {
          data: data,
          page: 'forms',
          form: page
        }
      });
    }).catch(err => console.error('Failed to open dialog', err));
  }

  deleteRow(row: any, eventType: string) {
    console.log(row, "data:----")
    try {
      const idx = (typeof row === 'object' && row !== null && typeof row._originalIndex === 'number') ? row._originalIndex : this.dataSource.indexOf(row);
      if (typeof idx === 'number' && idx >= 0) {
        this.selectedRadioIndex = idx;
        try { this.selectionChange.emit(idx); } catch (e) { }
      }
    } catch (e) { }

    let dialogWidth = '50vw';
    import('../../Pages/common/dialog/dialog.component').then(m => {
      this.dialog.open(m.DialogComponent, {
        width: dialogWidth,
        height: 'auto',
        maxWidth: '60vw',
        panelClass: 'custom-dialog-container',
        autoFocus: false,
        data: {
          page: 'forms',
          form: 'delete-record',
          eventType: eventType,
          id: row.Id
        }
      });
    }).catch(err => console.error('Failed to open dialog', err));
  }

  approveRow(data: any) {
    console.log(data, "datawhenapproved")
    let dialogWidth = '50vw';
    import('../../Pages/common/dialog/dialog.component').then(m => {
      this.dialog.open(m.DialogComponent, {
        width: dialogWidth,
        height: 'auto',
        maxWidth: '60vw',
        panelClass: 'custom-dialog-container',
        autoFocus: false,
        data: {
          page: 'forms',
          form: 'approve-record',
          id: data.BIN
        }
      });
    }).catch(err => console.error('Failed to open dialog', err));
  }

  declineRow(data: any) {
    let dialogWidth = '50vw';
    import('../../Pages/common/dialog/dialog.component').then(m => {
      this.dialog.open(m.DialogComponent, {
        width: dialogWidth,
        height: 'auto',
        maxWidth: '60vw',
        panelClass: 'custom-dialog-container',
        autoFocus: false,
        data: {
          page: 'forms',
          form: 'decline-record',
          id: data.BIN
        }
      });
    }).catch(err => console.error('Failed to open dialog', err));
  }

  addBankAccount() {
    let dialogWidth = '90vw';
    import('../../Pages/common/dialog/dialog.component').then(m => {
      this.dialog.open(m.DialogComponent, {
        width: dialogWidth,
        height: 'auto',
        maxWidth: '95vw',
        panelClass: 'custom-dialog-container',
        autoFocus: false,
        data: {
          page: 'forms',
          form: 'addBankAccount'
        }
      });
    }).catch(err => console.error('Failed to open dialog', err));
  }

  // showInfo(level?: string) {
  //   // Popup logic temporarily disabled — replaced by tooltip in template
  //   let dialogWidth = '50vw';
  //   import('../../Pages/common/dialog/dialog.component').then(m => {
  //     this.dialog.open(m.DialogComponent, {
  //       width: dialogWidth,
  //       height: 'auto',
  //       maxWidth: '60vw',
  //       panelClass: 'custom-dialog-container',
  //       autoFocus: false,
  //       data: {
  //         page: 'forms',
  //         form: "infopassword"
  //       }
  //     });
  //   }).catch(err => console.error('Failed to open dialog', err));
  // }

  // onSelectRow(index: any) {
  //   this.tableData = this.tableData.map((row, i) => {
  //     row[0] = i === index ? 'selected' : 'not-selected';
  //     return row;
  //   });
  // }

  addTeamMember() {
    let dialogWidth = '90vw';
    import('../../Pages/common/dialog/dialog.component').then(m => {
      this.dialog.open(m.DialogComponent, {
        width: dialogWidth,
        height: 'auto',
        maxWidth: '95vw',
        panelClass: 'custom-dialog-container',
        autoFocus: false,
        data: {
          page: 'forms',
          form: "addTeamMember"
        }
      });
    }).catch(err => console.error('Failed to open dialog', err));
  }

  get totalPages() {
    const count = Array.isArray(this.filteredRows) ? this.filteredRows.length : 0;
    return Math.max(1, Math.ceil(count / this.pageSize));
  }

  goToPage(page: number) {
    if (this.noPagination) {
      const full = this.filteredRows ?? [];
      if (this.filteringData instanceof MatTableDataSource) {
        this.filteringData.data = full;
      } else {
        this.filteringData = new MatTableDataSource(full);
      }
      if (this.sort) this.filteringData.sort = this.sort;

      this.showPagination = false;
      this.currentPage = 1;
      this.updateVisiblePages();
      this.cdr.detectChanges();
      return;
    }

    this.currentPage = Math.max(1, Math.min(page, this.totalPages));
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    const pageSlice = (this.filteredRows ?? []).slice(startIndex, endIndex);

    if (this.filteringData instanceof MatTableDataSource) {
      this.filteringData.data = pageSlice;
    } else {
      this.filteringData = new MatTableDataSource(pageSlice);
    }
    if (this.sort) this.filteringData.sort = this.sort;

    this.updateVisiblePages();
    this.cdr.detectChanges();
  }

  clearText() {
    this.searchText = '';
    const filtered = this.getFilteredData(this.dataSource);
    this.filteredRows = filtered;
    this.showPagination = !this.noPagination && (this.filteredRows.length > this.pageSize);
    this.currentPage = 1;
    this.goToPage(1);
  }

  async searchPrediction(e: any) {
    this.searchText = e.target?.value ?? '';
    const filtered = this.getFilteredData(this.dataSource);
    this.filteredRows = filtered;
    this.currentPage = 1;
    this.showPagination = this.filteredRows.length > this.pageSize;
    this.goToPage(1);
  }

  onFilterChange() {
    const filtered = this.getFilteredData(this.dataSource);
    this.filteredRows = filtered;
    this.currentPage = 1;
    this.showPagination = this.filteredRows.length > this.pageSize;
    this.goToPage(1);
  }

  getFilteredData(dataSource: any[]): any[] {
    const term = (this.searchText || '').trim().toLowerCase();

    return dataSource.filter((row: any) => {
      const matchSearch = term ? Object.values(row).some((val: any) => {
        if (!val) return false;
        return val.toString().replace(/<[^>]*>/g, '').toLowerCase().includes(term);
      }) : true;
      // apply status filter if selected
      let matchStatus = true;
      if (this.selectedStatuses.size > 0) {
        const col = this.filterColumn || 'Status';
        const raw = row[col];
        const selectedLower = Array.from(this.selectedStatuses).map(s => (s || '').toString().toLowerCase());
        if (this.teamMember) {
          // row may be comma-separated list of clients; match if any selected client exists
          if (raw === undefined || raw === null || String(raw).trim() === '') {
            matchStatus = selectedLower.includes('unassigned');
          } else {
            const parts = String(raw).split(',').map(p => p.trim()).filter(p => p !== '');
            matchStatus = parts.some(p => selectedLower.includes(p.toString().toLowerCase()));
          }
        } else {
          const val = raw !== undefined && raw !== null ? String(raw).trim() : '';
          if (val === '') {
            matchStatus = false;
          } else {
            matchStatus = selectedLower.includes(val.toString().toLowerCase());
          }
        }
      }
      return matchSearch && matchStatus;
    });

  }


  updateVisiblePages() {
    const maxVisible = 3;
    const pages: number[] = [];

    pages.push(1); // Always show first page

    let start = Math.max(2, this.currentPage - 1);
    let end = Math.min(this.totalPages - 1, this.currentPage + 1);

    if (start > 2) {
      pages.push(-1); // Ellipsis
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < this.totalPages - 1) {
      pages.push(-1); // Ellipsis
    }

    if (this.totalPages > 1) {
      pages.push(this.totalPages); // Always show last page
    }

    this.visiblePages = pages;
  }

  trackByIndex(index: number, item: string): number {
    return index;
  }

  isClientsList(): boolean {
    return this.tableInfo === "Client's List";
  }

  getDisplayedColumnsWithDelete(): string[] {
    const filteredColumns = this.displayedColumns.filter(col => col !== 'id' && col !== 'Id');
    if (this.isClientsList()) {
      return [...filteredColumns, 'deleteAction'];
    }
    return filteredColumns;
  }

  buildEmptyMessage(): string {
    if (this.dataLoadError) {
      return 'No data available';
    }

    const hasSearch = (this.searchText && this.searchText.trim().length > 0) || (this.externalSearchText && this.externalSearchText.trim().length > 0);
    const hasFilters = (this.selectedStatuses && this.selectedStatuses.size > 0) || (this.externalActiveFiltersCount && this.externalActiveFiltersCount > 0);

    if (hasSearch && hasFilters) {
      return 'No data available as per the filter and search criteria';
    } else if (hasSearch) {
      return 'No data available for the search term';
    } else if (hasFilters) {
      return 'No data available for the selected filters';
    } else {
      return 'No data available';
    }
  }

  shouldShowEmptyState(): boolean {
    const noRows = !this.hasData();

    if (this.dataLoadError) {
      return noRows;
    }

    if (this.dataLoadComplete && noRows) {
      return true;
    }

    const hasSearch = (this.searchText && this.searchText.trim().length > 0) || (this.externalSearchText && this.externalSearchText.trim().length > 0);
    const hasFilters = (this.selectedStatuses && this.selectedStatuses.size > 0) || (this.externalActiveFiltersCount && this.externalActiveFiltersCount > 0);

    if (!hasSearch && !hasFilters) {
      return false;
    }

    return noRows;
  }

  resetAllFilters(): void {
    this.searchText = '';
    this.selectedStatuses.clear();

    if (this.tableType === 'sorting' && this.showPagination) {
      // Reset to first page and apply pagination
      this.currentPage = 1;
      this.updateVisiblePages();
      const startIndex = 0;
      const endIndex = this.pageSize;
      const paginatedData = this.dataSource.slice(startIndex, endIndex);
      this.filteringData = new MatTableDataSource(paginatedData);
      this.filteringData.sort = this.sort;
    } else {
      this.filteringData = this.getFilteredData(this.dataSource);
    }

    this.cdr.detectChanges();

    try {
      this.filtersCleared.emit();
    } catch (err) {
      console.warn('filtersCleared emit failed', err);
    }
  }

  hasData(): boolean {
    // For teamMember and sorting tables, filteringData might be MatTableDataSource or array
    if (this.filteringData?.data) {
      return this.filteringData.data.length > 0;
    }
    // For radiotable and other cases, filteringData is a plain array
    if (Array.isArray(this.filteringData)) {
      return this.filteringData.length > 0;
    }
    return false;
  }
  async calculateColSpan() {
    const spansMap = new Map<number, number[]>();

    this.dataSource.forEach((row: any, rowIndex: number) => {
      const spans: number[] = [];
      let lastValue: any = null;
      let lastIndex = 0;
      let count = 0;
      let hasSpan = false;

      this.internalColumns.forEach((col: string, i: number) => {
        const cellValue = row[col] ? String(row[col]).trim() : '';

        if (cellValue && cellValue === lastValue) {
          count++;
          spans[lastIndex] = count;
          spans[i] = 0;
          hasSpan = true;
        } else {
          lastValue = cellValue;
          lastIndex = i;
          count = 1;
          spans[i] = 1;
        }
      });

      if (hasSpan) {
        spansMap.set(rowIndex, spans);
      }
    });

    return spansMap;
  }

  getColSpan(rowIndex: number, colIndex: number): number | null {
    const rowSpans = this.colSpans.get(rowIndex);
    if (!rowSpans) return null;
    const spanValue = rowSpans[colIndex];
    return spanValue && spanValue > 1 ? spanValue : null;
  }

  shouldHideCell(rowIndex: number, colIndex: number): boolean {
    const rowSpans = this.colSpans.get(rowIndex);
    return rowSpans ? rowSpans[colIndex] === 0 : false;
  }

  getCellUrls(rowIndex: number, colIndex: number): string[] {
    const cellConfig = this.resolveTableCell(rowIndex, colIndex);

    if (cellConfig === undefined || cellConfig === null) {
      return [];
    }

    // if (typeof cellConfig === 'string') {
    //   return cellConfig ? [cellConfig] : [];
    // }

    if (typeof cellConfig === 'string') {
      return this.normalizeUrlList(cellConfig ? [cellConfig] : []);
    }


    if (Array.isArray(cellConfig)) {
      if (this.isCellFragmentArray(cellConfig)) {
        // return cellConfig
        //   .filter(fragment => Boolean(fragment?.url))
        //   .map(fragment => fragment.url!)
        //   .filter(Boolean);

        return this.normalizeUrlList(
          cellConfig
            .filter(fragment => Boolean(fragment?.url))
            .map(fragment => fragment.url!)
            .filter(Boolean),
        );
      }
      // return (cellConfig as string[]).filter((url): url is string => typeof url === 'string' && !!url);
      return this.normalizeUrlList((cellConfig as string[]).filter((url): url is string => typeof url === 'string' && !!url));
    }

    if (this.isStructuredCell(cellConfig)) {
      // return (cellConfig.fragments ?? [])
      //   .filter(fragment => Boolean(fragment?.url))
      //   .map(fragment => fragment.url!)
      //   .filter(Boolean);

      return this.normalizeUrlList(
        (cellConfig.fragments ?? [])
          .filter(fragment => Boolean(fragment?.url))
          .map(fragment => fragment.url!)
          .filter(Boolean),
      );
    }

    return [];
  }

  getCellFragments(rowIndex: number, colIndex: number, rawValue: string): CellFragment[] {
    const cellConfig = this.resolveTableCell(rowIndex, colIndex);

    if (cellConfig === undefined || cellConfig === null) {
      return [];
    }

    if (this.isStructuredCell(cellConfig)) {
      return this.normalizeFragments(cellConfig.fragments ?? []);
    }

    if (this.isCellFragmentArray(cellConfig)) {
      return this.normalizeFragments(cellConfig);
    }

    const originalCell = this.tableData?.[rowIndex]?.[colIndex];
    if (Array.isArray(originalCell)) {
      const segmentUrls = this.extractSegmentUrls(cellConfig, originalCell.length);
      const arrayFragments = this.createFragmentsFromSegmentArray(originalCell, segmentUrls);
      if (arrayFragments.length) {
        return arrayFragments;
      }
    }

    const urls = this.getCellUrls(rowIndex, colIndex);

    const rawText = rawValue ?? this.normalizeCellValue(originalCell);

    if (!urls.length) {
      return [];
    }

    const fragments: CellFragment[] = [];
    const segments = rawText ? rawText.split(';') : [''];

    segments.forEach((segment, idx) => {
      const segmentFragments = this.createSegmentFragments(segment, urls[idx]);
      if (segmentFragments.length) {
        if (idx > 0 && fragments.length) {
          fragments.push({ text: ';' });
        }
        fragments.push(...segmentFragments);
      }
    });

    if (!fragments.length) {
      // return [{ text: this.stripHtml(rawText), url: urls[0] }];
      const normalizedUrl = this.resolveUrl(urls[0]).url;
      return normalizedUrl ? [{ text: this.stripHtml(rawText), url: normalizedUrl }] : [];
    }

    return fragments;
  }

  hasActiveFilters(): boolean {
    // Check if search text is present
    if (this.searchText && this.searchText.trim().length > 0) {
      return true;
    }
    // Check if any status filters are selected
    if (this.selectedStatuses && this.selectedStatuses.size > 0) {
      return true;
    }
    // External search text
    if (this.externalSearchText && this.externalSearchText.trim().length > 0) {
      return true;
    }
    // External filters count
    if (this.externalActiveFiltersCount && this.externalActiveFiltersCount > 0) {
      return true;
    }
    return false;
  }

  private resolveTableCell(rowIndex: number, colIndex: number): TableUrlCell | undefined {
    const entry = this.tableUrls?.[rowIndex];

    if (entry === undefined || entry === null) {
      return undefined;
    }

    if (typeof entry === 'string') {
      return entry;
    }

    if (Array.isArray(entry)) {
      return entry[colIndex] as TableUrlCell;
    }

    return undefined;
  }

  private isStructuredCell(value: unknown): value is StructuredCellConfig {
    return !!value && typeof value === 'object' && 'fragments' in (value as StructuredCellConfig);
  }

  private isCellFragmentArray(value: unknown): value is CellFragment[] {
    return Array.isArray(value) && value.every(fragment => !!fragment && typeof fragment === 'object' && 'text' in fragment);
  }

  private normalizeFragments(fragments: CellFragment[]): CellFragment[] {
    return fragments
      .filter(fragment => fragment && typeof fragment.text === 'string')
      .map(fragment => ({
        text: fragment.text,
        url: this.resolveUrl(fragment.url).url || undefined,
        isHtml: fragment.isHtml,
      }));
  }

  private createFragmentsFromSegmentArray(segments: string[], urls: (string | undefined)[]): CellFragment[] {
    if (!segments?.length) {
      return [];
    }

    const fragments: CellFragment[] = [];

    segments.forEach((segment, index) => {
      const text = segment ?? '';
      if (!text) {
        return;
      }

      const url = urls?.[index];
      const normalizedUrl = this.resolveUrl(url).url;
      if (normalizedUrl) {
        const linkFragments = this.createSegmentFragments(text, normalizedUrl);
        if (linkFragments.length) {
          fragments.push(...linkFragments);
        } else {
          fragments.push({ text: this.stripHtml(text), url: normalizedUrl });
        }
        return;
      }
      // if (url) {
      //   const linkFragments = this.createSegmentFragments(text, url);
      //   if (linkFragments.length) {
      //     fragments.push(...linkFragments);
      //   } else {
      //     fragments.push({ text: this.stripHtml(text), url });
      //   }
      //   return;
      // }

      const processed = this.processText(text);
      const containsHtml = processed !== text || /<[^>]+>/.test(text);
      fragments.push({
        text: containsHtml ? processed : text,
        isHtml: containsHtml,
      });
    });

    return fragments;
  }

  private extractSegmentUrls(cellConfig: TableUrlCell | undefined, segmentCount: number): (string | undefined)[] {
    const urls = Array(segmentCount).fill(undefined) as (string | undefined)[];

    if (!cellConfig || segmentCount === 0) {
      return urls;
    }

    if (this.isStructuredCell(cellConfig)) {
      (cellConfig.fragments ?? []).forEach((fragment, index) => {
        if (index < segmentCount) {
          urls[index] = fragment?.url;
        }
      });
      return urls;
    }

    if (this.isCellFragmentArray(cellConfig)) {
      cellConfig.forEach((fragment, index) => {
        if (index < segmentCount) {
          urls[index] = fragment?.url;
        }
      });
      return urls;
    }

    if (typeof cellConfig === 'string') {
      urls[0] = cellConfig.trim() ? cellConfig : undefined;
      return urls;
    }

    if (Array.isArray(cellConfig)) {
      (cellConfig as unknown[]).forEach((value, index) => {
        if (index >= segmentCount) {
          return;
        }
        if (typeof value === 'string') {
          const trimmed = value.trim();
          urls[index] = trimmed ? trimmed : undefined;
        }
      });
    }

    return urls;
  }

  private createSegmentFragments(segment: string, url?: string): CellFragment[] {
    const fragments: CellFragment[] = [];
    if (!segment && !url) {
      return fragments;
    }
    const normalizedUrl = this.resolveUrl(url).url;

    const prefixMatch = segment.match(/^\s*(?:and|or)?\s*/i);
    const prefix = prefixMatch ? prefixMatch[0] : '';
    const remainder = segment.slice(prefix.length);

    if (prefix) {
      fragments.push({ text: prefix });
    }

    if (!normalizedUrl) {
      if (remainder.trim()) {
        fragments.push({ text: this.processText(remainder), isHtml: true });
      }
      return fragments;
    }

    const label = this.stripHtml(remainder).trim();
    if (label) {
      fragments.push({ text: label, url: normalizedUrl });
    }

    return fragments;
  }

  private stripHtml(value: string): string {
    return (value ?? '').replace(/<[^>]+>/g, '');
  }

  processText(text: string | number | null | undefined): string {
    const normalized = text == null ? '' : String(text);
    return normalized.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
  }

  private normalizeCellValue(value: TableCellValue | undefined): string {
    if (Array.isArray(value)) {
      return value.join('');
    }
    return value ?? '';
  }

  getLinkSegments(text: string | undefined): { text: string; isWhitespace: boolean }[] {
    if (!text) {
      return [];
    }

    const parts = text.split(/(\s+)/); // capture whitespace tokens
    const segments = parts
      .filter(segment => segment !== '')
      .map(segment => ({
        text: segment,
        isWhitespace: /^\s+$/.test(segment),
      }));

    if (!segments.length) {
      return [{ text: text, isWhitespace: false }];
    }

    return segments;
  }

  onLinkClick(event: MouseEvent, url: string) {
    // console.log('onLinkClick called', { url, ctrl: event.ctrlKey, meta: event.metaKey, alt: event.altKey });
    // if (!url) return;
    const { url: resolvedUrl, wasRelative } = this.resolveUrl(url);
    if (!resolvedUrl) return;

    // Allow new-tab / new-window shortcuts
    if (event.ctrlKey || event.metaKey || event.shiftKey || (event as any).button === 1) {
      return;
    }

    if (!wasRelative && resolvedUrl.startsWith('/')) {
      try {
        event.preventDefault();
        event.stopPropagation();
        const nav = this.router.navigateByUrl(resolvedUrl);
        if (nav && typeof (nav as any).then === 'function') {
          (nav as any).then((navigated: boolean) => {
            // console.log('router.navigateByUrl result', navigated, url);
            if (!navigated) {
              // console.warn('Router did not navigate — falling back to window.location', url);
              window.location.href = resolvedUrl;
            }
          }).catch((err: unknown) => {
            // console.error('router.navigateByUrl error', err);
            window.location.href = resolvedUrl;
          });
        } else {
          // router returned non-promise — fallback after brief delay
          setTimeout(() => { window.location.href = resolvedUrl; }, 0);
        }
      } catch (err: unknown) {
        console.error('onLinkClick navigation error', err);
        window.location.href = resolvedUrl;
      }
      return;
    }

    // External link — let browser handle, but stop propagation
    try { event.stopPropagation(); } catch (e) { /* ignore */ }
  }

  onMouseDownNavigate(event: MouseEvent, url: string) {
    // Trigger navigation on mousedown as an early fallback when click is blocked
    try {
      const { url: resolvedUrl, wasRelative } = this.resolveUrl(url);
      if (!resolvedUrl) return;
      // If modifier keys used, let default behavior (open in new tab) occur
      if (event.ctrlKey || event.metaKey || event.shiftKey || (event as any).button === 1) return;
      // For internal routes, try router navigation immediately
      if (wasRelative) {
        try {
          event.preventDefault();
          event.stopPropagation();
          window.location.href = resolvedUrl;
        } catch (e) {
          // ignore
        }
        return;
      }

      if (resolvedUrl.startsWith('/')) {
        try {
          event.preventDefault();
          event.stopPropagation();
          // attempt navigation but don't await here to avoid blocking mousedown
          this.router.navigateByUrl(resolvedUrl).catch((err: unknown) => {
            // fallback to location
            try { window.location.href = resolvedUrl; } catch (e) { /* ignore */ }
          });
        } catch (e) {
          try { window.location.href = resolvedUrl; } catch (err) { /* ignore */ }
        }
      }
    } catch (e) {
      // ignore errors on mousedown
    }
  }

  logTdClick(heading: string, rowIndex: number, event: MouseEvent) {
    // lightweight logger method used by template to avoid direct `console` calls from template
    // keeps template AOT-safe and TypeScript happy
    try {
      // eslint-disable-next-line no-console
      // console.log('td clicked', heading, rowIndex, event);
    } catch (e) {
      // ignore
    }
  }

  onCellClick(event: MouseEvent, actualRowIndex: number, colIndex: number, rawValue?: string) {
    // If user clicked an actual anchor or interactive element, let it handle the action
    const target = event.target as HTMLElement | null;
    if (target) {
      const tag = target.tagName.toLowerCase();
      // If the click was inside an anchor, try to use that anchor's href
      const anchor = (target.closest && target.closest('a')) as HTMLAnchorElement | null;
      if (anchor && anchor.href) {
        // compute href relative to app base: use anchor.getAttribute to get original value
        const rawHref = anchor.getAttribute('href') || anchor.href;
        this.onLinkClick(event, rawHref);
        return;
      }

      // If click is on other interactive elements, let them handle it
      if (tag === 'button' || tag === 'mat-icon') {
        return;
      }
    }

    // Try to resolve a URL for this cell and navigate programmatically
    try {
      const fragments = this.getCellFragments(actualRowIndex, colIndex, rawValue || '');
      if (fragments && fragments.length) {
        const firstWithUrl = fragments.find(f => !!f.url);
        if (firstWithUrl && firstWithUrl.url) {
          // reuse onLinkClick logic for navigation
          this.onLinkClick(event, firstWithUrl.url);
        }
      } else {
        // fallback: check tableUrls structure
        const urls = this.getCellUrls(actualRowIndex, colIndex);
        if (urls && urls.length && urls[0]) {
          this.onLinkClick(event, urls[0]);
        }
      }
    } catch (e) {
      // ignore
    }
  }


  private resolveUrl(rawUrl?: string): { url: string; wasRelative: boolean } {
    const trimmed = (rawUrl ?? '').trim();
    if (!trimmed) {
      return { url: '', wasRelative: false };
    }

    const hasProtocol = /^(https?:)?\/\//i.test(trimmed) || /^mailto:/i.test(trimmed) || /^tel:/i.test(trimmed);
    if (hasProtocol) {
      const normalized = trimmed.startsWith('http') ? trimmed : `https:${trimmed}`;
      const isPortalLink = this.usePortalBaseForRelativeLinks && this.portalBaseUrl ? normalized.startsWith(this.portalBaseUrl) : false;
      return { url: normalized, wasRelative: isPortalLink };
    }

    if (trimmed.startsWith('/')) {
      if (this.usePortalBaseForRelativeLinks && this.portalBaseUrl) {
        return { url: `${this.portalBaseUrl}/${trimmed.replace(/^\/+/, '')}`, wasRelative: true };
      }
      return { url: trimmed, wasRelative: false };
    }

    return { url: trimmed, wasRelative: false };
  }

  private normalizeUrlList(urls: (string | undefined)[]): string[] {
    return urls
      .map(url => this.resolveUrl(url).url)
      .filter((url): url is string => !!url);
  }

  getLinkTarget(url?: string): '_self' | '_blank' {
    if (!url) return '_self';
    const resolved = this.resolveUrl(url);
    if (this.usePortalBaseForRelativeLinks && resolved.wasRelative) return '_blank';
    if (resolved.url.startsWith('/')) return '_self';
    if (this.portalBaseUrl && resolved.url.startsWith(this.portalBaseUrl)) return this.usePortalBaseForRelativeLinks ? '_blank' : '_self';
    return '_blank';
  }

  private openCellLink(url: string, event: MouseEvent): void {
    const { url: resolvedUrl, wasRelative } = this.resolveUrl(url);
    if (!resolvedUrl) return;
    try {
      event?.preventDefault();
      event?.stopPropagation();
    } catch (e) { /* ignore */ }
    const target = (this.usePortalBaseForRelativeLinks && wasRelative) ? '_blank' : (resolvedUrl.startsWith('/') ? '_self' : '_blank');
    try {
      window.open(resolvedUrl, target);
    } catch (e) {
      if (target === '_self') {
        try { window.location.href = resolvedUrl; } catch (_) { }
      }
    }
  }

  onCellLinkClick(url: string, event: MouseEvent): void {
    if (this.skipNextClick) {
      this.skipNextClick = false;
      return;
    }
    this.openCellLink(url, event);
  }

  onCellLinkMouseDown(url: string, event: MouseEvent): void {
    const { url: resolvedUrl, wasRelative } = this.resolveUrl(url);
    if (!resolvedUrl) return;
    const btn = (event as any)?.button ?? 0;
    if (btn !== 0) return;
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    this.skipNextClick = true;
    if (this.usePortalBaseForRelativeLinks && wasRelative) {
      try {
        event.preventDefault();
        event.stopPropagation();
        window.open(resolvedUrl, '_blank');
        return;
      } catch (e) {
        // ignore and fallback
      }
    }
    this.openCellLink(resolvedUrl, event);
  }

  isRightAligned(heading: string): boolean {
    // console.log('Checking right alignment for heading:', heading);
    return this.rightAlignedColumns.includes(heading);
  }

  isCenterAligned(heading: string): boolean {
    return this.centerAlignedColumns.includes(heading);
  }

}


